import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Maximize2, Minimize2, ChevronsUp, ChevronUp, ChevronDown } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import { getWidget, SIZE_COLS } from './registry';
import FeatureSpotlight, { useFeatureSpotlight } from '../components/FeatureSpotlight';

const GRID_COLS = 4;
const GRID_GAP = 16;
const SPAN_TO_SIZE = { 1: 'sm', 2: 'md', 3: 'lg', 4: 'xl' };

// Customizable widget grid with iOS home-screen-style edit mode:
//   - widgets jiggle gently while editing (CSS keyframes)
//   - small dark "minus" badge in the top-left removes the widget
//   - small dark size chip in the bottom-right cycles sm → md → lg → xl
//   - drag the whole widget to reorder (desktop). Mobile uses tap-to-cycle
//     position via the minus + size chips, since HTML5 touch DnD is poor.
//
// Outside edit mode: a tiny fullscreen icon appears on hover (subtle).
const SIZE_ORDER = ['sm', 'md', 'lg', 'xl'];

export default function DashboardGrid({ layout, ctx, editing = false, onLayoutChange }) {
  const isMobile = useIsMobile();
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const gridRef = useRef(null);
  const visible = layout.filter(l => !l.hidden);

  if (visible.length === 0) return <DashboardGridEmpty editing={editing} />;

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch {}
  };
  const handleDragOver = (e, id) => {
    if (!dragId || dragId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overId !== id) setOverId(id);
  };
  const handleDrop = (e, id) => {
    e.preventDefault();
    if (!dragId || dragId === id) { setDragId(null); setOverId(null); return; }
    onLayoutChange(reorderBefore(layout, dragId, id));
    setDragId(null);
    setOverId(null);
  };
  const handleDragEnd = () => { setDragId(null); setOverId(null); };

  return (
    <div
      ref={gridRef}
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : `repeat(${GRID_COLS}, 1fr)`,
        gap: GRID_GAP,
        marginTop: 16,
        // Slight padding so the protruding minus/size badges don't get clipped
        padding: editing ? '10px 6px' : 0,
        transition: 'padding 0.2s',
      }}
    >
      {visible.map((entry, idx) => (
        <WidgetSlot
          key={entry.id}
          entry={entry}
          ctx={ctx}
          editing={editing}
          isMobile={isMobile}
          isFirst={idx === 0}
          isLast={idx === visible.length - 1}
          isDragging={dragId === entry.id}
          isDropTarget={overId === entry.id && dragId && dragId !== entry.id}
          onDragStart={(e) => handleDragStart(e, entry.id)}
          onDragOver={(e) => handleDragOver(e, entry.id)}
          onDragLeave={() => { if (overId === entry.id) setOverId(null); }}
          onDrop={(e) => handleDrop(e, entry.id)}
          onDragEnd={handleDragEnd}
          onCycleSize={() => onLayoutChange(cycleSize(layout, entry.id))}
          onResizeSpan={(span) => onLayoutChange(setSizeBySpan(layout, entry.id, span))}
          onMoveStep={(dir) => onLayoutChange(moveStep(layout, entry.id, dir))}
          onMoveTop={() => onLayoutChange(moveToTop(layout, entry.id))}
          onRemove={() => onLayoutChange(hideEntry(layout, entry.id))}
          onConfigChange={(config) => onLayoutChange(setConfig(layout, entry.id, config))}
          onToggleCollapse={() => onLayoutChange(toggleCollapsed(layout, entry.id))}
          spotlight={idx === 0}
          gridRef={gridRef}
        />
      ))}
    </div>
  );
}

function DashboardGridEmpty({ editing }) {
  const { t } = useTranslation();
  return (
    <div
      className="card"
      style={{
        marginTop: 16,
        textAlign: 'center',
        padding: '32px 20px',
        color: 'var(--c-text-muted)',
        fontSize: 13,
      }}
    >
      {editing
        ? t('dashboard.widgets.emptyEditing', 'No widgets visible. Tap "Add widget" to add one.')
        : t('dashboard.widgets.empty', 'No widgets to show — tap Customize to add some.')}
    </div>
  );
}

function WidgetSlot({
  entry, ctx, editing, isMobile, isFirst, isLast,
  isDragging, isDropTarget,
  onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
  onCycleSize, onResizeSpan, onMoveStep, onMoveTop, onRemove, onConfigChange,
  onToggleCollapse, spotlight, gridRef,
}) {
  const { t } = useTranslation();
  const spot = useFeatureSpotlight('dashboard_widget_collapse_v1');
  const widget = getWidget(entry.id);
  const [fullscreen, setFullscreen] = useState(false);
  const slotRef = useRef(null);
  if (!widget) return null;
  const WidgetIcon = widget.icon;
  const Component = widget.Component;
  const collapsed = !!entry.collapsed;
  const spanCols = isMobile ? 1 : (collapsed ? 1 : (SIZE_COLS[entry.size] || 2));

  // Stop click events inside the widget from triggering navigation while
  // editing — but allow the badges/chip themselves to fire.
  const blockInteraction = editing
    ? { pointerEvents: 'none', userSelect: 'none' }
    : {};

  return (
    <div
      ref={slotRef}
      className={editing ? `widget-jiggle${isDragging ? ' is-dragging' : ''}` : ''}
      draggable={editing && !isMobile}
      onDragStart={editing ? onDragStart : undefined}
      onDragOver={editing ? onDragOver : undefined}
      onDragLeave={editing ? onDragLeave : undefined}
      onDrop={editing ? onDrop : undefined}
      onDragEnd={editing ? onDragEnd : undefined}
      style={{
        gridColumn: `span ${spanCols}`,
        position: 'relative',
        minWidth: 0,
        opacity: isDragging ? 0.4 : 1,
        cursor: editing && !isMobile ? 'grab' : 'default',
        transition: 'opacity 0.15s, box-shadow 0.15s',
        // Brand outline ring when something is about to be dropped on this slot
        outline: isDropTarget ? '3px solid var(--c-brand)' : 'none',
        outlineOffset: isDropTarget ? '4px' : '0',
        borderRadius: 12,
      }}
    >
      {/* Drop indicator bar to the left of this widget */}
      {isDropTarget && (
        <div
          style={{
            position: 'absolute', left: -12, top: 4, bottom: 4,
            width: 4, borderRadius: 2, background: 'var(--c-brand)',
            boxShadow: '0 0 8px var(--c-brand)',
            zIndex: 10, pointerEvents: 'none',
          }}
        />
      )}

      {editing ? (
        <>
          <button
            className="widget-remove-badge"
            onClick={onRemove}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.preventDefault()}
            title="Remove widget"
          >
            <Minus size={14} strokeWidth={3} />
          </button>
          <ResizeChip
            size={entry.size || 'md'}
            isMobile={isMobile}
            slotRef={slotRef}
            gridRef={gridRef}
            onCycleSize={onCycleSize}
            onResizeSpan={onResizeSpan}
          />
          {/* "Al principio" está en desktop TAMBIÉN, no sólo mobile: arrastrar un
              widget desde el fondo de la grilla hasta el tope es el movimiento más
              común y el más molesto de hacer a mano. Un click y va primero. */}
          {(!isFirst || (isMobile && !isLast)) && (
            <div style={{ position: 'absolute', top: -6, right: 28, display: 'flex', gap: 4, zIndex: 10 }}>
              {!isFirst && <StepBadge dir="top" onClick={onMoveTop} />}
              {isMobile && !isFirst && <StepBadge dir="up" onClick={() => onMoveStep('up')} />}
              {isMobile && !isLast && <StepBadge dir="down" onClick={() => onMoveStep('down')} />}
            </div>
          )}
        </>
      ) : !collapsed && (
        <>
          <SlotIconButton
            right={30}
            title={t('dashboard.collapseWidget')}
            onClick={onToggleCollapse}
            innerRef={spotlight ? spot.anchorRef : undefined}
          >
            <ChevronUp size={13} />
          </SlotIconButton>
          <FullscreenButton onClick={() => setFullscreen(true)} />
        </>
      )}

      {collapsed ? (
        <div
          className="card"
          onClick={editing ? undefined : onToggleCollapse}
          title={t('dashboard.expandWidget')}
          style={{
            height: '100%', margin: 0, display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 14px', cursor: editing ? 'inherit' : 'pointer', minWidth: 0,
          }}
        >
          {WidgetIcon && <WidgetIcon size={14} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />}
          <span style={{
            fontSize: 12, fontWeight: 600, color: 'var(--c-text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {t(widget.titleKey)}
          </span>
          <ChevronDown size={14} style={{ marginLeft: 'auto', color: 'var(--c-text-muted)', flexShrink: 0 }} />
        </div>
      ) : (
        <div style={blockInteraction}>
          <Component ctx={ctx} config={entry.config || null} onConfigChange={onConfigChange} />
        </div>
      )}

      {spotlight && (
        <FeatureSpotlight
          anchorRef={spot.anchorRef}
          show={spot.shouldShow && !editing && !collapsed}
          onDismiss={spot.dismiss}
          title={t('dashboard.collapseSpotlightTitle')}
          body={t('dashboard.collapseSpotlightBody')}
          placement="bottom"
        />
      )}

      {fullscreen && (
        <WidgetFullscreen onClose={() => setFullscreen(false)} title={widget.titleKey}>
          <Component ctx={ctx} config={entry.config || null} onConfigChange={onConfigChange} fullscreen />
        </WidgetFullscreen>
      )}
    </div>
  );
}

// Bottom-right chip in edit mode. Two interactions:
//   - plain click  → cycle size (sm → md → lg → xl)  [also the mobile path]
//   - drag sideways → live-resize the column span (1–4) with snapping  [desktop]
// We disambiguate by movement threshold: if the pointer moved > 4px it was a
// drag, and we swallow the trailing click so it doesn't also cycle.
function ResizeChip({ size, isMobile, slotRef, gridRef, onCycleSize, onResizeSpan }) {
  const drag = useRef({ active: false, moved: false, startX: 0 });

  const computeSpan = (clientX) => {
    const grid = gridRef?.current;
    const slot = slotRef?.current;
    if (!grid || !slot) return null;
    const gw = grid.getBoundingClientRect().width;
    const colW = (gw - (GRID_COLS - 1) * GRID_GAP) / GRID_COLS;
    const slotLeft = slot.getBoundingClientRect().left;
    const width = clientX - slotLeft;
    let span = Math.round((width + GRID_GAP) / (colW + GRID_GAP));
    return Math.max(1, Math.min(GRID_COLS, span));
  };

  const onPointerDown = (e) => {
    if (isMobile) return; // mobile resizes via tap-to-cycle only
    e.stopPropagation();
    drag.current = { active: true, moved: false, startX: e.clientX };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    if (Math.abs(e.clientX - drag.current.startX) > 4) drag.current.moved = true;
    if (!drag.current.moved) return;
    const span = computeSpan(e.clientX);
    if (span) onResizeSpan?.(span);
  };
  const onPointerUp = (e) => {
    if (!drag.current.active) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    drag.current.active = false;
  };
  const onClick = (e) => {
    e.stopPropagation();
    if (drag.current.moved) { drag.current.moved = false; return; } // was a resize drag
    onCycleSize?.();
  };

  return (
    <button
      className="widget-size-chip"
      style={{ cursor: isMobile ? 'pointer' : 'ew-resize' }}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onMouseDown={(e) => e.stopPropagation()}
      onDragStart={(e) => e.preventDefault()}
      title={isMobile ? 'Cambiar tamaño' : 'Arrastrá para redimensionar · click para ciclar'}
    >
      {(size || 'md').toUpperCase()}
    </button>
  );
}

function StepBadge({ dir, onClick }) {
  const { t } = useTranslation();
  const title = dir === 'top'
    ? t('dashboard.widgets.moveTop', 'Move to top')
    : dir === 'up'
      ? t('dashboard.widgets.moveUp', 'Move up')
      : t('dashboard.widgets.moveDown', 'Move down');
  return (
    <button
      onClick={onClick}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        width: 22, height: 22, borderRadius: '50%',
        background: dir === 'top' ? 'var(--c-brand)' : 'var(--c-text)',
        color: dir === 'top' ? '#fff' : 'var(--c-bg)',
        border: '2px solid var(--c-bg)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        fontSize: 12, fontWeight: 700, padding: 0,
      }}
      title={title}
    >
      {dir === 'top' ? <ChevronsUp size={13} strokeWidth={3} /> : dir === 'up' ? '↑' : '↓'}
    </button>
  );
}

// Shared chrome for the small icon buttons that live in a widget's top-right
// corner outside edit mode (collapse, fullscreen). Same hover treatment, only
// the horizontal offset changes.
function SlotIconButton({ onClick, title, children, right = 8, innerRef }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      ref={innerRef}
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'absolute', top: 8, right, zIndex: 2,
        padding: 5, border: 'none', borderRadius: 6,
        background: hover ? 'var(--c-surface-2)' : 'transparent',
        color: hover ? 'var(--c-text)' : 'var(--c-text-muted)',
        opacity: hover ? 1 : 0.4,
        cursor: 'pointer',
        transition: 'opacity 0.15s, background 0.15s, color 0.15s',
      }}
    >
      {children}
    </button>
  );
}

function FullscreenButton({ onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      title="Fullscreen"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'absolute', top: 8, right: 8, zIndex: 2,
        padding: 5, border: 'none', borderRadius: 6,
        background: hover ? 'var(--c-surface-2)' : 'transparent',
        color: hover ? 'var(--c-text)' : 'var(--c-text-muted)',
        opacity: hover ? 1 : 0.4,
        cursor: 'pointer',
        transition: 'opacity 0.15s, background 0.15s, color 0.15s',
      }}
    >
      <Maximize2 size={12} />
    </button>
  );
}

function WidgetFullscreen({ children, onClose, title }) {
  const { t } = useTranslation();
  return (
    <div
      onMouseDown={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)', padding: 24, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 1200, margin: '0 auto',
          background: 'var(--c-bg)', borderRadius: 12, padding: 16,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 14, color: 'var(--c-text-muted)', fontWeight: 600 }}>
            {t(title)}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <Minimize2 size={13} /> {t('common.close', 'Close')}
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Layout mutation helpers ──
function reorderBefore(layout, dragId, targetId) {
  const next = layout.slice();
  const fromIdx = next.findIndex(l => l.id === dragId);
  const toIdx = next.findIndex(l => l.id === targetId);
  if (fromIdx < 0 || toIdx < 0) return layout;
  const [item] = next.splice(fromIdx, 1);
  const insertIdx = next.findIndex(l => l.id === targetId);
  next.splice(insertIdx, 0, item);
  return next;
}

// Manda el widget al principio de la grilla conservando el resto del orden.
// Trabaja sobre el layout COMPLETO (incluye ocultos) para no perderlos.
function moveToTop(layout, id) {
  const idx = layout.findIndex(l => l.id === id);
  if (idx <= 0) return layout;
  const next = layout.slice();
  const [entry] = next.splice(idx, 1);
  next.unshift(entry);
  return next;
}

function moveStep(layout, id, dir) {
  const idx = layout.findIndex(l => l.id === id);
  if (idx < 0) return layout;
  const next = layout.slice();
  const isVisible = (entry) => !entry.hidden;
  let neighborIdx = idx;
  if (dir === 'up') {
    for (let i = idx - 1; i >= 0; i--) { if (isVisible(next[i])) { neighborIdx = i; break; } }
  } else {
    for (let i = idx + 1; i < next.length; i++) { if (isVisible(next[i])) { neighborIdx = i; break; } }
  }
  if (neighborIdx === idx) return layout;
  [next[idx], next[neighborIdx]] = [next[neighborIdx], next[idx]];
  return next;
}

function cycleSize(layout, id) {
  return layout.map(l => {
    if (l.id !== id) return l;
    const cur = SIZE_ORDER.indexOf(l.size || 'md');
    return { ...l, size: SIZE_ORDER[(cur + 1) % SIZE_ORDER.length] };
  });
}

function setSizeBySpan(layout, id, span) {
  const size = SPAN_TO_SIZE[span] || 'md';
  return layout.map(l => (l.id === id && l.size !== size ? { ...l, size } : l));
}

// Collapse is deliberately NOT part of edit mode. Hiding a widget you don't need
// right now used to mean entering the grid editor, removing it, and adding it back
// later — too heavy for a "not today" gesture. A collapsed widget stays in place as
// a one-line bar (so you never lose track of it) and shrinks to a single column so
// several of them tuck away together.
function toggleCollapsed(layout, id) {
  return layout.map(l => (l.id === id ? { ...l, collapsed: !l.collapsed } : l));
}

function hideEntry(layout, id) {
  return layout.map(l => (l.id === id ? { ...l, hidden: true } : l));
}

function setConfig(layout, id, config) {
  return layout.map(l => (l.id === id ? { ...l, config: { ...(l.config || {}), ...config } } : l));
}
